<template>
	<div
		:class="`wrapper wrapper--article ${customClass}`"
		:article-id="articleID"
		:tour-id="tourID"
		:previous-article-id="previousArticleID"
		:exhibition-id="exhibitionID"
		ref="wrapper"
		:shareable="articleIsShareable"
		:loading="loading"
		:is-current="isCurrent"
		:is-history="!!historyKey"
		role="article"
	>
		<UiSearch :scope="inferScope()" v-if="/-list$/.test(articleID)" />
		<div
			v-else
			ref="content"
			v-html="htmlContent"
			v-show="!loading"
			:article-id="articleID"
			:exhibition-id="exhibitionID"
			style="width: 100%"
		></div>
		<UiShare v-if="articleIsShareable" :exhibitionID="exhibitionID" :articleID="articleID" />
	</div>
</template>

<style lang="css"></style>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import UiShare from './ui/share.vue';
import UiSearch from './ui/search.vue';
import { isValidUuid } from '@/tools/helpers';
import { setUrlToDataSource } from '@/tools/setUrlToDataSource';
import { actionTypes, exhibitionTypes, articleTypes } from '@/types';
import contentUI from '@/contentUI';
import { logger } from '@/tools';
import { mapStateNested } from '@/tools/helpers';
import { SEARCH_SCOPES } from '@/tools/consts';
import { highlightGlossary } from '@/tools/glossary';
import { loadMediaObjects } from '@/tools/loadMediaObjects';
import * as imageLoader from '@/tools/imageLoader';

export default {
	components: {
		UiSearch,
		UiShare,
	},
	props: {
		isCurrent: { type: Boolean },
		isInModal: { type: Boolean },
		articleID: String,
		previousArticleID: String,
		tourID: String,
		exhibitionID: String,
		historyKey: String,
		classes: Object,
		customClass: String,
	},
	data() {
		return {
			swipeTargets: {
				leftArticleProperties: null,
				rightArticleProperties: null,
			},
			htmlContent: '',
			loading: false,
			isContentUIinited: false,
			imageObserver: null,
			contentUIStoreWatchers: [], // Content UI Storewatchers are stored here
		};
	},

	computed: {
		...mapState(['dataSourceURL']),
		...mapStateNested('dataSourceCFG', ['exhibitionMap', 'configWebClient']),
		...mapGetters(['currentLanguage']),
		articleIsShareable() {
			const exhibition = this.exhibitionMap[this.exhibitionID];
			if (!exhibition) return false;
			const article = exhibition.articleMap[this.articleID];
			return article && article.isShareable;
		},
	},

	watch: {
		currentLanguage() {
			if (this.articleID && this.exhibitionID) {
				this.renderHtmlContent();
			}
		},
		/**
		 * This watcher checks if the popertyle articleID is changing and if it loads the new content
		 */
		async articleID(newID, oldID) {
			if (newID === oldID) {
				return;
			}
			this.swipeTargets = {
				leftArticleProperties: null,
				rightArticleProperties: null,
			};
			if (!newID) {
				this.destroyContentUI();
				this.htmlContent = '';
				this.isContentUIinited = true;
			} else {
				// reset Swipe Targets because new content is loaded ;
				this.renderHtmlContent();
			}
		},
		isCurrent(isCurrent, oldCurrent) {
			if (isCurrent) {
				if (this.isContentUIinited) {
					// if ContentUI is already initialised possible swipTargets are up to date and View is now Main View so send them to swip-view
					if (this.swipeTargets.leftArticleProperties || this.swipeTargets.rightArticleProperties) {
						this.$emit('swipe-link-update', this.swipeTargets);
					}
					this.emitNavigationTheme();
					contentUI.positionUpdated(this);
				}
			}
			if (oldCurrent) {
				logger.log('= deactivated =', this.articleID, this.$store.state.articleTransition);
				// force clear the swipe transform
				this.$el.style.transform = '';
			}
			contentUI.positionUpdated(this);
		},
		htmlContent() {
			this.$nextTick(() => {
				Array.from(this.$el.querySelectorAll('[data-url]')).forEach(($el) => {
					$el.addEventListener('click', (event) => {
						event.stopPropagation();
						this.openUrl({ url: $el.dataset.url });
					});
				});
			});
		},
	},
	methods: {
		...mapActions([actionTypes.openUrl, actionTypes.addToMediaObjectMap]),
		async renderHtmlContent() {
			this.isContentUIinited = false;
			if (this.articleID && this.exhibitionID) {
				this.loading = true;
				this.htmlContent = await this.$store.dispatch(actionTypes.loadContent, {
					articleID: this.articleID,
					exhibitionID: this.exhibitionID,
					tourID: this.tourID,
				});

				const currentState = {
					articleID: this.$props.articleID + '', // properties are no primitive data. Needs copying the value for change detection.
					exhibitionID: this.$props.exhibitionID + '',
					isCurrent: !!this.isCurrent,
				};
				this.$nextTick(() => {
					if (
						currentState.articleID !== this.$props.articleID ||
						currentState.exhibitionID !== this.$props.exhibitionID
					) {
						// state of articleView has changed HTML content will be updated again in the next tik.
						// contentUI must not be initialised because component state has changed.

						return;
					}

					this.initContentUI();
					if (currentState.isCurrent) {
						// contentUI possible updates swipeTargets so send them to MainContent.vue
						if (this.swipeTargets.leftArticleProperties || this.swipeTargets.rightArticleProperties) {
							this.$emit('swipe-link-update', this.swipeTargets);
						}
						this.emitNavigationTheme();
					}
					this.isContentUIinited = true;
					this.loading = false;
					this.$emit('article-loaded', this.swipeTargets);
				});
			} else {
				logger.error(
					`Article component is created with inproper parameters articleID: ${this.articleID} exhibitionID: ${this.exhibitionID}`
				);
			}
		},
		loadMediaObjects() {
			const mediaArray = loadMediaObjects(this.$el.querySelector('.wrapper--page'), this.dataSourceURL);
			this.addToMediaObjectMap({ mediaObjectArray: mediaArray });
		},
		emitNavigationTheme() {
			let theme;
			if (/^tour-[a-zA-Z0-9]*-list/.test(this.articleID)) {
				theme = this.articleID;
			} else if (this.articleID === 'article-list') {
				theme = `list-${this.exhibitionID}`;
			} else {
				const el = this.$el.querySelector('.wrapper--page');
				theme = el ? el.getAttribute('navigation-theme') || 'default' : 'default';
			}
			this.$emit('navigation-update', theme);
		},
		updatedMediaURLs() {
			const elementsWithSources = [
				...this.$el.querySelectorAll('img[src], audio[src], video[src], source[src]'),
			];
			elementsWithSources.forEach(
				(el) => (el.src = setUrlToDataSource(el.getAttribute('src'), this.dataSourceURL))
			);
		},

		initContentUI() {
			logger.log('initContentUI for ' + this.articleID);
			if (this.$refs.content) {
				// only for content not for ui list
				imageLoader.watch({
					container: this.$refs.content,
					observer: this.imageObserver,
				});
			}
			// add page-theme to article
			const el = this.$el.querySelector('.wrapper--page');
			const theme = el ? el.getAttribute('page-theme') || 'default' : 'default';
			this.$el.setAttribute('page-theme', theme);

			this.loadMediaObjects();
			this.updatedMediaURLs();

			contentUI.init(this);

			this.glossary();

			this.$emit('contentUIReady', this);
		},

		destroyContentUI() {
			contentUI.destroy(this);
			imageLoader.unwatch({
				container: this.$refs.content,
				observer: this.imageObserver,
			});
			// destroy UIStoreWatchers
			this.contentUIStoreWatchers.forEach((unwatch) => {
				unwatch();
			});
		},

		glossary() {
			[...this.$el.querySelectorAll('.element--text')].forEach(($el) => {
				// to Prevent highlighting the term it is own glossary term article
				highlightGlossary($el, { ignoreTermKey: this.articleID });
			});
		},

		inferScope() {
			if (this.$props.exhibitionID === exhibitionTypes.root && this.articleID === 'exhibition-list')
				return SEARCH_SCOPES.exhibitions;
			if (this.$props.articleID === 'tour-list') return SEARCH_SCOPES.tours;
			if (
				this.$props.exhibitionID === exhibitionTypes.glossary &&
				this.$props.articleID === articleTypes.articleList
			)
				return SEARCH_SCOPES.glossary;
			if (this.$props.articleID.match(/^tour-/)) return SEARCH_SCOPES.tour;
			if (
				this.$props.exhibitionID === exhibitionTypes.calendar &&
				this.$props.articleID === articleTypes.articleList
			)
				return SEARCH_SCOPES.calendar;
			if (this.$props.articleID === articleTypes.articleList) return SEARCH_SCOPES.articles;
		},

		isValidUuid(string) {
			return isValidUuid(string);
		},
		/**
		 * This method infers from route parameters the correct search scope to show correct data
		 */
	},
	async created() {},
	mounted() {
		this.imageObserver = imageLoader.init({ root: this.$el });
		if (this.articleID && this.exhibitionID) {
			this.renderHtmlContent();
		}
	},
};
</script>
